import { useCallback, useEffect, useRef } from "react";
import { useLocation } from "react-router-dom";
import { useAppSelector } from "Redux/hooks";
import { setUserRecentActivity } from "Services/user";

const HEARTBEAT_INTERVAL = 60 * 1000;

const UserActivityHeartbeat = () => {
  const location = useLocation();

  const userData = useAppSelector(
    (state) => state.user.userData
  );

  const lastSentAt = useRef(0);

  const sendActivity = useCallback(
    (force = false) => {
      if (!userData?.id) {
        return;
      }

      const now = Date.now();

      if (
        !force &&
        now - lastSentAt.current < HEARTBEAT_INTERVAL
      ) {
        return;
      }

      lastSentAt.current = now;

      void setUserRecentActivity({
        current_page:
          window.location.pathname +
          window.location.search,
        current_page_title: document.title || "",
      }).catch(() => {
        // Activity tracking must never interrupt the user.
      });
    },
    [userData?.id]
  );

  useEffect(() => {
    if (!userData?.id) {
      return;
    }

    sendActivity(true);
  }, [
    location.pathname,
    location.search,
    userData?.id,
    sendActivity,
  ]);

  useEffect(() => {
    if (!userData?.id) {
      return;
    }

    const handleActivity = () => {
      sendActivity(false);
    };

    const handleVisibilityChange = () => {
      if (document.visibilityState === "visible") {
        sendActivity(false);
      }
    };

    window.addEventListener(
      "mousedown",
      handleActivity
    );

    window.addEventListener(
      "keydown",
      handleActivity
    );

    window.addEventListener(
      "touchstart",
      handleActivity,
      { passive: true }
    );

    window.addEventListener(
      "scroll",
      handleActivity,
      { passive: true }
    );

    document.addEventListener(
      "visibilitychange",
      handleVisibilityChange
    );

    return () => {
      window.removeEventListener(
        "mousedown",
        handleActivity
      );

      window.removeEventListener(
        "keydown",
        handleActivity
      );

      window.removeEventListener(
        "touchstart",
        handleActivity
      );

      window.removeEventListener(
        "scroll",
        handleActivity
      );

      document.removeEventListener(
        "visibilitychange",
        handleVisibilityChange
      );
    };
  }, [userData?.id, sendActivity]);

  return null;
};

export default UserActivityHeartbeat;